<template>
	<article
		class="view-content-card"
		v-bind:class="{
			'type-video': contentModel.isVideo(),
		}"
		v-on:click="Handle_OnClick"
	>
		<div class="thumbnail">
			<img
				v-bind:alt="contentModel.getTitle()"
				v-bind:src="contentModel.getThumbnailUrl()"
				v-if="contentModel.getThumbnailUrl()"
			/>
		</div>

		<div class="details">
			<h3 class="title">{{ contentModel.getTitle() }}</h3>

			<div class="source-line">
				<img
					class="source-icon"
					v-bind:alt="contentModel.getTitle()"
					v-bind:src="contentModel.getIconUrl()"
					v-if="contentModel.hasIcon()"
				/>
				<span class="date">{{ formattedDate }}</span>
			</div>

			<!-- <div class="meta">
				<span
					class="badge"
					v-bind:class="getBadgeClass(tagModel)"
					v-bind:key="tagModel.id"
					v-for="tagModel in contentModel.tags.models"
				>
					{{ tagModel.getTag() }}
				</span>
			</div> -->
		</div>
	</article>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';

	/**
	 * Preview card for content items in the content listing. Displays a
	 * thumbnail, title, date, source icon, and tag badges.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Content
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewContentCard extends ViewBase {
		/**
		 * Returns relative time for recent content, or a formatted date for older items
		 *
		 * @return string
		 */
		protected get formattedDate(): string {
			if (this.contentModel.isMonthOld('published_at')) {
				return this.contentModel.getPublishedAt('MMM D, YYYY');
			}

			return this.contentModel.getPublishedAtTimeAgo();
		}

		/**
		 * Content model to display
		 *
		 * @type ChalkySticks.Model.Content
		 */
		@Prop({
			required: true,
		})
		public contentModel!: ChalkySticks.Model.Content;

		/**
		 * Map of tag names to badge CSS modifier classes
		 *
		 * @type Record<string, string>
		 */
		protected badgeClassMap: Record<string, string> = {
			news: 'type-primary',
			review: 'type-warning',
			tutorial: 'type-success',
			youtube: 'type-danger',
		};

		/**
		 * Returns the badge modifier class for a given tag model
		 *
		 * @param ChalkySticks.Model.ContentTag tagModel
		 * @return string
		 */
		protected getBadgeClass(tagModel: ChalkySticks.Model.ContentTag): string {
			const tag = tagModel.getTag();

			return this.badgeClassMap[tag] || 'type-info';
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		protected async Handle_OnClick(e: Event): Promise<void> {
			this.$emit('select', this.contentModel);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-content-card {
		background: var(--chalky-blue-4);
		border-radius: 0.5rem;
		cursor: pointer;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		transition: box-shadow 0.2s ease-in-out, transform 0.2s ease-in-out;

		.thumbnail {
			aspect-ratio: 16 / 9;
			background: var(--chalky-blue-3);
			overflow: hidden;
			position: relative;

			img {
				height: 100%;
				object-fit: cover;
				width: 100%;
			}
		}

		.details {
			display: flex;
			flex-direction: column;
			flex-grow: 1;
			gap: 0.5rem;
			padding: 0.75rem;
		}

		.source-line {
			align-items: center;
			color: var(--chalky-grey-2);
			display: flex;
			font-size: 0.8rem;
			gap: 0.5rem;
		}
	}

	// State
	// ---------------------------------------------------------------------------

	.view-content-card:active {
		box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
		transform: translateY(-2px);
	}
</style>
